/**
 * Error Handler Utility
 * Provides consistent error handling and user-friendly error messages
 */

class ErrorHandler {
  static getErrorMessage(error, context = 'cart') {
    // Network errors
    if (!navigator.onLine) {
      return 'You appear to be offline. Please check your internet connection and try again.';
    }

    // Timeout errors
    if (error.name === 'TimeoutError' || error.message.includes('timeout')) {
      return 'The request is taking longer than expected. Please try again.';
    }

    // Fetch errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return 'Unable to connect to the server. Please check your connection and try again.';
    }

    // HTTP status errors
    if (error.status) {
      switch (error.status) {
        case 400:
          return 'Invalid request. Please check your selection and try again.';
        case 401:
          return 'Please log in to continue.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'The requested item could not be found.';
        case 422:
          return error.message || 'Unable to process your request. Please check your selection and try again.';
        case 429:
          return 'Too many requests. Please wait a moment and try again.';
        case 500:
          return 'Server error. Please try again in a few moments.';
        case 502:
        case 503:
        case 504:
          return 'Service temporarily unavailable. Please try again later.';
        default:
          return 'An unexpected error occurred. Please try again.';
      }
    }

    // Context-specific errors
    switch (context) {
      case 'cart':
        return 'Unable to add item to cart. Please try again.';
      case 'update':
        return 'Unable to update cart. Please refresh the page and try again.';
      case 'remove':
        return 'Unable to remove item from cart. Please try again.';
      case 'modal':
        return 'Unable to load cart information. Please try again.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }

  static handleError(error, context = 'cart', showToast = true) {
    const message = this.getErrorMessage(error, context);

    console.error(`Error in ${context}:`, error);

    if (showToast && window.ToastManager) {
      window.ToastManager.error('Error', message);
    }

    return message;
  }

  static isRetryableError(error) {
    // Network errors are usually retryable
    if (!navigator.onLine) return true;
    if (error.name === 'TypeError' && error.message.includes('fetch')) return true;
    if (error.name === 'TimeoutError') return true;

    // HTTP status codes that are retryable
    if (error.status) {
      return [408, 429, 500, 502, 503, 504].includes(error.status);
    }

    return false;
  }

  static async retryOperation(operation, maxRetries = 3, delay = 1000) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;

        if (!this.isRetryableError(error) || attempt === maxRetries) {
          throw error;
        }

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay * attempt));
      }
    }

    throw lastError;
  }
}

// Export for use in other scripts
window.ErrorHandler = ErrorHandler;
